// package imports
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import AgilityContentFetch from '@agility/content-fetch';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useSession } from 'next-auth/react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import DOMPurify from 'dompurify';

// local imports
import PleaseWait from './PleaseWait';
import { ROLE_CONST } from '../../services/roleUtils';
import { parseQueryString } from '../../services/stringUtils';
import YesNoDialog from './subcomponents/YesNoDialog';
import {
  COMMON_ROUTES,
  CONSTANTS,
  ERROR_MESSAGES,
  PATHS,
  PRODUCTS,
} from '../../services/constants';
import { callApi, BadRequestError } from 'services/apiWrapper';
import {
  logClientException,
  logServerException,
} from 'appinsights/clientAppInsights';
import { generateUUID } from '../../services/stringUtils';
import { hasModuleAccess, isUserSubscriber } from 'services/subscriptionUtils';
import { addDataLayerEvent } from 'services/dataLayerUtils';
import {
  getSessionStoreString,
  removeSigningOutState,
  removeSessionStoreItem,
} from 'services/storageUtils';
import {
  SharedHeaderDesktop,
  SharedHeaderDesktopWrapper,
} from './SiteHeaderComponents/SiteHeaderStyledComponents';
import SearchButton from './SiteHeaderComponents/SearchButton';
import useSearchContext from './SiteHeaderComponents/SearchContext';
import SearchBar from './SiteHeaderComponents/SearchBar';
import { SiteHeaderType } from './SiteHeaderComponents/SiteHeaderTypes';
import { SharedHeaderType } from './SiteHeaderComponents/SharedHeaderTypes';
import { SessionSubscriptionType } from 'types';
import { SiteFooterType } from './SiteHeaderComponents/SiteFooterTypes';
import DesktopMegaMenu from './SiteHeaderComponents/DesktopMegaMenu';
import useLoadingOrSigningOut from 'services/hooks/useLoadingOrSigningOut';
import MobileMegaMenu from './SiteHeaderComponents/MobileMegaMenu';
import { AgilityList, LessonPlan } from 'types/AgilityTypes';
import { weekOfYear } from 'services/dateUtils';
import { getWMSRedirectPath } from '../../services/redirectUtils';

const Alert = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof MuiAlert>
>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';

type SiteHeaderGlobalData = {
  header: {
    sharedHeaderFields: SharedHeaderType;
    siteHeaderFields: SiteHeaderType;
    termsAndConditions: string;
    lessonPlanOfTheWeek: LessonPlan;
  };
  footer: SiteFooterType;
};

const SiteHeader = ({
  globalData,
}: {
  globalData: SiteHeaderGlobalData;
  handleHeaderClick: (e) => void;
}) => {
  const header = globalData?.header;
  const isSearchOpen = useSearchContext((s) => s.openMenu) === 'search';
  const toggleSearchOpen = useSearchContext((s) => s.toggle);
  const { setSearchCategory } = useSearchContext();

  const [openProfileDialoge, setOpenProfileDialog] = useState(false);
  const [openExpiredTrial, setOpenExpiredTrial] = useState(false);
  const [expiredSub, setExpiredSub] = useState<{
    open: boolean;
    product?: string;
    sub?: SessionSubscriptionType | null;
  }>({
    open: false,
    product: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const router = useRouter();

  const { data: session, status } = useSession();
  const [isWaiting, setIsWaiting] = useState(useLoadingOrSigningOut(status));

  // this useEffect checks if the user is trying to
  // navigate away from the page without completing their profile
  // note: I don't like this approach and would prefer to use a modal
  useEffect(() => {
    const confirmationMessage =
      'Please complete your profile before visiting any other areas of the site!';
    const beforeUnloadHandler = (e) => {
      if (
        window.location.pathname === '/complete-profile' &&
        getSessionStoreString('profile_complete') !== 'true'
      ) {
        e.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    const handleRouteChange = (url, { _shallow }) => {
      if (
        window.location.pathname === '/complete-profile' &&
        getSessionStoreString('profile_complete') !== 'true'
      ) {
        router.events.emit('routeChangeError');
        alert(confirmationMessage);
        throw `Route change to "${url}" was aborted (this error can be safely ignored). See https://github.com/zeit/next.js/issues/2476.`;
      }
    };

    if (openProfileDialoge) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      router.events.on('routeChangeStart', handleRouteChange);
      router.events.on('beforeHistoryChange', handleRouteChange);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('beforeHistoryChange', handleRouteChange);
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('beforeHistoryChange', handleRouteChange);
    };
  }, [router, openProfileDialoge]);

  useEffect(() => {
    if (!status || status === 'loading') return;
    if (
      session &&
      router.asPath === '/' &&
      isUserSubscriber(session, CONSTANTS.PROGRAMS.WMS) &&
      window.sessionStorage.getItem('signing_in') !== 'true'
    ) {
      // user is a subscriber, redirect to subscriber landing page
      router.push(PATHS.SUBSCRIBER_LANDING_PAGE);
      return;
    }
    // legacy redirect
    const redirectPath = getWMSRedirectPath({
      status,
      asPath: router.asPath,
      query: router.query,
      session,
      isUserSubscriber,
      WMS_PROGRAM_CODE: CONSTANTS.PROGRAMS.WMS,
    });
    if (redirectPath) {
      router.replace(redirectPath);
    }
  }, [router, session, status]);

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError') {
      doSignin(); // Force sign in to hopefully resolve error
    }

    let needsProfile = false;

    if (session?.user) {
      // user is logged in

      if (!session?.user?.state) {
        // state not set
        needsProfile = true;
      } else if (!session?.user?.position) {
        //position not set
        needsProfile = true;
      } else if (
        session.user.role === ROLE_CONST.BoysTownAdmin ||
        session.user.role === ROLE_CONST.ContentReviewer
      ) {
        // no subscription necessary for admin
      } else if (
        !session.user.subscriptions ||
        session.user.subscriptions.length === 0
      ) {
        // no current subscription
        needsProfile = true;
      } else if (
        session.user.subscriptions &&
        session.user.subscriptions.filter(
          (s: { message?: string }) =>
            s?.message === ERROR_MESSAGES.TrialExpired,
        )?.length > 0
      ) {
        // have an expired subscription
        console.log('has expired trial sub');
        setOpenExpiredTrial(true);
      } else if (
        session.user.subscriptions &&
        session.user.subscriptions.filter(
          (s: { message?: string }) =>
            s?.message === ERROR_MESSAGES.SubscriptionExpired,
        )?.length > 0 &&
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem('renewing') !== 'true'
      ) {
        // have an expired subscription
        console.log('has expired sub');
        const expiredSub = session.user.subscriptions.filter(
          (s: { message?: string }) =>
            s?.message === ERROR_MESSAGES.SubscriptionExpired,
        )[0];
        setExpiredSub({
          open: true,
          product: PRODUCTS.find((p) => p.id === expiredSub.product)?.name,
          sub: expiredSub,
        });
      } else if (
        session.user.subscriptions &&
        session.user.subscriptions.filter((s) => s?.is_active === true)
          ?.length > 0
      ) {
        // have a current subscription
      } else if (
        session.user.subscriptions &&
        session.user.subscriptions.filter((s) => s?.is_active === true)
          ?.length === 0
      ) {
        // no subscription
        needsProfile = true;
      }

      if (needsProfile) {
        setOpenProfileDialog(true);
      } else {
        removeSessionStoreItem('profile_complete');
      }

      if (window.sessionStorage.getItem('signing_in') === 'true') {
        if (session?.user) {
          const event = {
            event: 'login',
            userId: session.user.id,
            user_profession: session.user.position,
            user_email: session.user.email,
          };
          addDataLayerEvent(event);
        }
        if (needsProfile) {
          // no redirects - should go to complete profile
        } else if (
          window &&
          window.sessionStorage.getItem('redirect') &&
          window.sessionStorage.getItem('redirect')?.startsWith('/') === true
        ) {
          const sanitizedRedirect = DOMPurify.sanitize(
            window.sessionStorage.getItem('redirect') || '',
          );
          window.sessionStorage.removeItem('redirect');
          window.sessionStorage.removeItem('signing_in');
          router.push(sanitizedRedirect);
        } else if (session && window.location.pathname === '/') {
          if (hasModuleAccess(session, CONSTANTS.PROGRAMS.WMS)) {
            router.push(PATHS.SUBSCRIBER_LANDING_PAGE);
          } else if (hasModuleAccess(session, 'ai')) {
            router.push('/administrative-intervention');
          } else if (hasModuleAccess(session, 'shss')) {
            router.push('/safe-healthy-secondary-schools');
          } else if (hasModuleAccess(session, 'scm')) {
            router.push('/specialized-classroom-management');
          } else if (hasModuleAccess(session, 'pas')) {
            router.push('/positive-alternatives-to-suspension');
          } else if (hasModuleAccess(session, 'explit')) {
            router.push('/expedition-literacy');
          } else if (hasModuleAccess(session, 'psfnd')) {
            router.push('/positive-foundations');
          } else {
            router.push('/free-resources');
          }
          sessionStorage.removeItem('signing_in');
        }
      }
    }
  }, [session]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window?.location?.search) {
        //https://ntr-app-tst-01.azurewebsites.net?did=93f0ddbb-3f00-4f60-a832-81b76cbcfee4&st=NE
        const qs = parseQueryString(window?.location?.search);
        if (qs?.did) {
          window?.localStorage?.setItem('did', qs.did);
        }
        if (qs?.st) {
          window?.localStorage?.setItem('st', qs.st);
        }
        if (qs?.sid) {
          window?.localStorage?.setItem('sid', qs.sid);
        }
        if (qs?.utm_source) {
          window?.sessionStorage?.setItem('utm_source', qs.utm_source);
        }
        if (qs?.utm_medium) {
          window?.sessionStorage?.setItem('utm_medium', qs.utm_medium);
        }
        if (qs?.utm_campaign) {
          window?.sessionStorage?.setItem('utm_campaign', qs.utm_campaign);
        }
        if (qs?.utm_content) {
          window?.sessionStorage?.setItem('utm_content', qs.utm_content);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.location.href.indexOf('/complete-profile') === -1 &&
      openProfileDialoge
    ) {
      router.push('/complete-profile');
    }
  }, [openProfileDialoge]);

  useEffect(() => {
    if (status !== 'loading') {
      removeSigningOutState();
      setIsWaiting(false);
    }
  }, [status]);

  const doSignin = () => {
    removeSigningOutState();
    router.push('/signin');
  };

  if (!header?.siteHeaderFields?.fields) {
    return <header></header>;
  }

  const onUpgradeSubscription = () => {
    setOpenExpiredTrial(false);
    router.push(COMMON_ROUTES.PricingPage);
  };

  const continueWithFreeAccount = async () => {
    await addFreeSubscription();
  };

  const addFreeSubscription = async () => {
    try {
      setIsLoading(true);
      let body = {
        emailAddress: session?.user.email,
        product: 'free',
        status: 'active',
        end_date: new Date(
          new Date().setFullYear(new Date().getFullYear() + 99),
        ).getTime(),
        id: generateUUID(),
      };

      // save user
      await callApi('/api/db/user-subscription', 'POST', body);
      setIsLoading(false);
      setOpenProfileDialog(false);
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch (error) {
      logClientException(error);
      setIsLoading(false);
      if (error instanceof BadRequestError) {
        setErrorMessage(error.message);
        return;
      } else {
        setErrorMessage(defaultErrorMessage);
      }
    }
  };

  const onRenewSubscription = () => {
    setExpiredSub({ open: false });
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('renewing', 'true');
    }
    router.push(COMMON_ROUTES.PricingPage);
  };

  const continueWithExpiredProduct = async () => {
    try {
      setIsLoading(true);
      let body = {
        emailAddress: session?.user.email,
        status: 'expired',
        sub_id: expiredSub.sub?.id,
      };

      // update subscription
      await callApi('/api/db/user-subscription', 'PUT', body);
      if (!session?.user.subscriptions?.find((s) => s.product === 'free')) {
        await addFreeSubscription();
      }
      setIsLoading(false);
      setExpiredSub({ open: false, product: '', sub: null });
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch (error) {
      logClientException(error);
      setIsLoading(false);
      if (error instanceof BadRequestError) {
        setErrorMessage(error.message);
        return;
      } else {
        setErrorMessage(defaultErrorMessage);
      }
    }
  };

  const handleAlertClose = (_event: React.SyntheticEvent) => {
    setErrorMessage(null);
  };

  return (
    <>
      <PleaseWait isLoading={isLoading} />
      <YesNoDialog
        open={openExpiredTrial}
        content="Your trial has expired. Would like to upgrade your subscription?"
        yesClose={onUpgradeSubscription}
        noClose={continueWithFreeAccount}
      />
      <YesNoDialog
        open={expiredSub.open}
        content={`Your "${expiredSub.product}" subscription has expired. Would like to renew your subscription?`}
        yesClose={onRenewSubscription}
        noClose={continueWithExpiredProduct}
      />
      <Snackbar
        open={errorMessage !== null}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Desktop Menu */}
      <SharedHeaderDesktopWrapper>
        <SharedHeaderDesktop id="shared-header-desktop">
          <SharedHeaderNav
            iconUrl={header.sharedHeaderFields.fields.mainIcon?.url}
          >
            <li>
              <a
                className="primary-nav-link"
                href={header.sharedHeaderFields.fields.mainLink?.href}
                target="_blank"
                rel="noreferrer"
              >
                {header.sharedHeaderFields.fields.mainLabel}
              </a>
            </li>
            {header.sharedHeaderFields.fields.sites.map((link, index) => {
              return (
                <li key={`desktop-${index}`}>
                  <a
                    className={
                      link.fields.label?.toLocaleLowerCase() ===
                        'liftwithboystown.org' ||
                      link.fields.label?.toLocaleLowerCase() ===
                        'lift with boys town'
                        ? 'current-link'
                        : 'primary-nav-link'
                    }
                    href={link.fields.link.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {link.fields.label}
                  </a>
                </li>
              );
            })}
            <li>
              <SearchButton
                isActive={isSearchOpen}
                onClick={() => {
                  toggleSearchOpen('search');
                  setSearchCategory('');
                }}
              />
            </li>
          </SharedHeaderNav>
        </SharedHeaderDesktop>
        <SearchBar
          isActive={isSearchOpen}
          searchTerms={header.siteHeaderFields.fields.searchTerms}
        />
      </SharedHeaderDesktopWrapper>
      <header className="sticky top-0 bg-white z-[1299] border-b border-gray-300">
        {!isWaiting && (
          <DesktopMegaMenu
            headerFields={header.siteHeaderFields}
            session={session}
            lessonPlanOfTheWeek={header.lessonPlanOfTheWeek}
          />
        )}

        {!isWaiting && (
          <Box
            sx={{
              '@media (min-width: 1024px)': {
                display: 'none',
              },
            }}
          >
            <MobileMegaMenu
              headerFields={header.siteHeaderFields}
              sharedHeaderFields={header.sharedHeaderFields}
              session={session}
            />
          </Box>
        )}
      </header>
    </>
  );
};

SiteHeader.getCustomInitialProps = async function ({ agility, languageCode }) {
  // set up api
  const api = agility;

  // return object
  let headerData = {
    sharedHeaderFields: {} as SharedHeaderType,
    siteHeaderFields: {} as SiteHeaderType,
    termsAndConditions: '',
    lessonPlanOfTheWeek: {} as LessonPlan,
  };

  try {
    // set up api for content hub to fetch shared header config
    const hubApi = AgilityContentFetch.getApi({
      guid: process.env.CONTENT_HUB_GUID,
      apiKey: api.config.isPreview
        ? process.env.CONTENT_HUB_API_PREVIEW_KEY
        : process.env.CONTENT_HUB_API_FETCH_KEY,
      isPreview: api.config.isPreview,
    });

    const sharedHeader = await hubApi.getContentList({
      referenceName: 'sharedheader',
      locale: languageCode,
      contentLinkDepth: 3,
      expandAllContentLinks: true,
    });

    headerData.sharedHeaderFields = sharedHeader.items[0];

    // try to fetch our site header
    let header = await api.getContentList({
      referenceName: 'siteheader',
      locale: languageCode,
      take: 1,
      contentLinkDepth: 5,
      expandAllContentLinks: true,
    });

    // if we have a header, set as content item
    if (header && header.items && header.items.length > 0) {
      headerData.siteHeaderFields = header.items[0];

      // else return null
    } else {
      return null;
    }

    let page = await api.getPage({
      pageID: 100,
      locale: languageCode,
    });

    if (page?.zones?.MainContentZone?.length > 0) {
      const mod = page?.zones?.MainContentZone.find(
        (m) => m.module === 'RichTextArea',
      );
      if (mod?.item?.fields?.textblob) {
        headerData.termsAndConditions = mod?.item?.fields?.textblob;
      }
    }

    // lesson plan of the week
    const lessonPlanList: AgilityList<LessonPlan> = await api.getContentList({
      referenceName: 'lessonplans',
      locale: languageCode,
      take: 250,
      contentLinkDepth: 5,
      expandAllContentLinks: true,
    });

    if (lessonPlanList) {
      const weekNum = weekOfYear() - 1;
      const planCount = lessonPlanList.items?.length;
      if (planCount > weekNum) {
        headerData.lessonPlanOfTheWeek = lessonPlanList.items[weekNum];
      } else {
        const rem = weekNum % planCount;
        headerData.lessonPlanOfTheWeek = lessonPlanList.items[rem];
      }
    }
  } catch (error) {
    await logServerException(error);
    if (console) console.error('Could not load site header item.', error);
    return null;
  }

  // return clean object...
  return headerData;
};

export default SiteHeader;

export const SharedHeaderNav = styled('ul', {
  shouldForwardProp: (prop) => prop !== 'iconUrl',
})<{ iconUrl?: string }>(({ theme, iconUrl }) => ({
  //[theme.breakpoints.down('md')]: {
  '@media (max-width: 1024px)': {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
  },
  //[theme.breakpoints.up('lg')]: {
  '@media (min-width: 1024px)': {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    border: 'none',
    paddingLeft: '2.5rem',
    paddingRight: '2.5rem',
    maxWidth: '1392px',
    margin: '0 auto',
    '& > li': {
      backgroundColor: 'transparent',
      color: '#f1f1f1',
      borderRight: 'thin solid rgba(255, 255, 255, 0.2)',
      '& > a': {
        textDecoration: 'none',
        fontSize: '14px',
        lineHeight: '20px',
        textTransform: 'uppercase',
        textAlign: 'left',
        fontWeight: '600',
        padding: '12px 0.75rem',
        border: 'none',
        display: 'block',
      },
    },
    '& > li:last-child': {
      backgroundColor: 'transparent',
      border: 'none',
      marginLeft: 'auto',
    },
  },
  '& > li a.current-link': {
    backgroundColor: theme.palette.gold.medium,
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '0.90rem',
  },
  '& > li:first-of-type a': {
    display: 'flex',
    alignItems: 'center',
  },
  '& > li:first-of-type a:before': {
    content: '""',
    backgroundImage: `url(${iconUrl})`,
    width: '20px',
    height: '20px',
    backgroundSize: 'cover',
    marginRight: '5px',
    filter: 'brightness(0) invert(1)',
  },
}));
